/**
 * Field Type Registry for Custom CRUD Field Components.
 *
 * @deprecated Use componentRegistry.ts instead. This module is maintained
 * for backwards compatibility and delegates to the unified registry.
 *
 * Migration:
 * - Old: import { registerFieldType } from '@ja/crud/services/fieldTypeRegistry';
 * - New: import { registerFieldComponent } from '@ja/crud/services/componentRegistry';
 *
 * @example
 * // Preferred (new API):
 * import { registerFieldComponent } from '@ja/crud/services/componentRegistry';
 * registerFieldComponent('weeklyAvailability', () => import('./WeeklyAvailabilityField.vue'), 'workforce');
 *
 * // Legacy (still works):
 * import { registerFieldType } from '@ja/crud/services/fieldTypeRegistry';
 * registerFieldType('weeklyAvailability', () => import('./WeeklyAvailabilityField.vue'), 'workforce');
 */

import type { AsyncComponentLoader, Component } from 'vue';

import {
    getComponentInfo,
    getFieldComponent,
    getRegisteredComponents,
    hasFieldComponent,
    registerFieldComponent,
    type ComponentEntry,
} from './componentRegistry';

// Re-export types for backwards compatibility
export type FieldComponentModule = { default: Component };
export type AsyncFieldComponent = AsyncComponentLoader<Component>;

/**
 * @deprecated Use registerFieldComponent from componentRegistry instead.
 */
export function registerFieldType(
    fieldType: string,
    component: FieldComponentModule | AsyncFieldComponent,
    packageName: string = 'unknown',
): void {
    registerFieldComponent(fieldType, component as ComponentEntry, packageName);
}

/**
 * @deprecated Use getFieldComponent from componentRegistry instead.
 */
export function getFieldType(fieldType: string): FieldComponentModule | AsyncFieldComponent | undefined {
    return getFieldComponent(fieldType) as FieldComponentModule | AsyncFieldComponent | undefined;
}

/**
 * @deprecated Use hasFieldComponent from componentRegistry instead.
 */
export function hasFieldType(fieldType: string): boolean {
    return hasFieldComponent(fieldType);
}

/**
 * @deprecated Use getRegisteredComponents('field') from componentRegistry instead.
 */
export function getRegisteredFieldTypes(): string[] {
    return getRegisteredComponents('field');
}

/**
 * @deprecated Use getComponentInfo('field', fieldType) from componentRegistry instead.
 */
export function getFieldTypeInfo(fieldType: string): { component: ComponentEntry; package: string } | undefined {
    const info = getComponentInfo('field', fieldType);
    if (!info) return undefined;
    return {
        component: info.component,
        package: info.package,
    };
}
